<template>
  <tr>
    <td>
      <MDBCheckbox :modelValue="isSelected" @update:modelValue="(val) => emit('select', item.id, val)" />
    </td>
    <td>{{ parseDateLong(item.dateCreated) }}</td>
    <td>{{ item.firstName }} {{ item.lastName }}</td>
    <td>{{ item.email }}</td>
    <td>{{ item.country }}</td>
    <td>{{ item.contactInformation }}</td>
    <td>
      <template v-if="item.status === 'Pending'">
        <MDBBadge class="bg-warning text-white" style="font-size: 13px; width: 90px">New</MDBBadge>
      </template>
      <template v-if="item.status === 'Approved'">
        <MDBBadge class="bg-success text-white" style="font-size: 13px; width: 90px">Approved</MDBBadge>
      </template>
      <template v-else-if="item.status === 'Rejected'">
        <MDBBadge class="bg-danger text-white" style="font-size: 13px; width: 90px">Rejected</MDBBadge>
      </template>
    </td>
    <td class="text-nowrap">
      <MDBDropdown v-model="isOpen">
        <button type="button" @click.stop="emit('toggleDropdown', item.id)"
          class="btn-md border-0 bg-transparent primary-text ms-4">
          <i class="fas fa-ellipsis-v fa-lg pe-none"></i>
        </button>

        <MDBDropdownMenu :animation="false" class="position-absolute end-0 p-2">
          <MDBDropdownItem tag="div" href="#" class="fw-bold" style="user-select: none">Actions
          </MDBDropdownItem>
          <MDBDropdownItem tag="div" href="#" @click.stop="viewModal = true">View Application
          </MDBDropdownItem>
          <MDBDropdownItem tag="div" href="#" @click.stop="handleViewContract">
            View Contract
          </MDBDropdownItem>
          <MDBDropdownItem tag="div" href="#" @click.stop="approveModal = true" :disabled="item.status !== 'Pending'">
            Approve</MDBDropdownItem>
          <MDBDropdownItem tag="div" href="#" @click.stop="rejectModal = true" :disabled="item.status !== 'Pending'">
            Reject</MDBDropdownItem>
        </MDBDropdownMenu>
      </MDBDropdown>
    </td>
  </tr>

  <MDBModal id="viewModal" tabindex="-1" labelledby="viewModalLabel" v-model="viewModal" class="ps-0">
    <MDBModalHeader>
      <MDBModalTitle id="viewModalLabel" class="fw-bold">
        View Application Details
      </MDBModalTitle>
    </MDBModalHeader>
    <MDBModalBody class="p-4 d-flex flex-column gap-4 overflow-auto" style="max-height: 75vh;">
      <div class="d-flex gap-4">
        <MDBInput label="First Name" :modelValue="item.firstName" readonly />
        <MDBInput label="Last Name" :modelValue="item.lastName" readonly />
      </div>
      <div class="d-flex gap-4">
        <MDBInput label="Email" :modelValue="item.email" readonly />
        <MDBInput label="Country" :modelValue="item.country" readonly />
      </div>
      <MDBInput label="Contact Information" :modelValue="item.contactInformation" readonly />
      <MDBInput label="Your PayPal Email" :modelValue="item.paypalEmail" readonly />
      <h6 class="mb-0">Did you record this video? <span class="fw-bold">{{ item.recorded ? 'Yes' : 'No' }}</span></h6>
      <template v-if="item.recorded">
        <MDBInput label="Recording Location" :modelValue="item.recordingLocation" readonly />
      </template>
      <template v-if="item.videoLinks">
        <div>
          <label class="form-label fw-bold">Video Link(s)</label>
          <ul class="ms-2 mb-0">
            <li v-for="(link, i) in item.videoLinks" :key="i">
              <a :href="link.url" target="_blank" class="text-primary">
                {{ link.url }}
              </a>
            </li>
          </ul>
        </div>
      </template>
      <template v-if="item.clipUrl">
        <div>
          <label class="form-label fw-bold">Footage</label>
          <video :src="item.clipUrl" controls className="w-100" />
        </div>
      </template>
    </MDBModalBody>
  </MDBModal>

  <MDBModal id="approveModal" tabindex="-1" labelledby="approveModalLabel" v-model="approveModal" class="ps-0">
    <MDBModalHeader>
      <MDBModalTitle id="approveModalLabel" class="fw-bold">
        Approve Clip Submission
      </MDBModalTitle>
    </MDBModalHeader>
    <MDBModalBody>Are you sure you want to approve this clip submission?</MDBModalBody>
    <MDBModalFooter class="gap-2 py-3">
      <MDBBtn class="fw-bold" @click="approveModal = false" size="sm">
        Close
      </MDBBtn>
      <MDBBtn class="fw-bold" @click="handleApproveConfirm" color="primary" size="sm">
        Confirm
      </MDBBtn>
    </MDBModalFooter>
  </MDBModal>

  <MDBModal id="rejectModal" tabindex="-1" labelledby="rejectModalLabel" v-model="rejectModal" class="ps-0">
    <MDBModalHeader>
      <MDBModalTitle id="rejectModalLabel" class="fw-bold">
        Reject Clip Submission
      </MDBModalTitle>
    </MDBModalHeader>
    <MDBModalBody>Are you sure you want to reject this clip submission?</MDBModalBody>
    <MDBModalFooter class="gap-2 py-3">
      <MDBBtn class="fw-bold" @click="rejectModal = false" size="sm">
        Close
      </MDBBtn>
      <MDBBtn class="fw-bold" @click="handleRejectConfirm" color="danger" size="sm">
        Confirm
      </MDBBtn>
    </MDBModalFooter>
  </MDBModal>
</template>

<script setup>
import {
  MDBBadge,
  MDBBtn,
  MDBCheckbox,
  MDBDropdown,
  MDBDropdownItem,
  MDBDropdownMenu,
  MDBInput,
  MDBModal,
  MDBModalBody,
  MDBModalFooter,
  MDBModalHeader,
  MDBModalTitle,
} from "mdb-vue-ui-kit";
import { computed, defineEmits, defineProps, ref } from "vue";
import { parseDateLong } from "../../../helpers/parseDate";
import { UpdateClaimSubmissionStatus } from "../../../services/ClaimSubmission/UpdateClaimSubmission";

const props = defineProps({
  item: Object,
  isSelected: Boolean,
  openDropdownId: [Number, String, null],
});

const emit = defineEmits(["onAction", "select", "toggleDropdown"]);

const isOpen = computed(() => props.openDropdownId === props.item.id);

// const toastStore = useToastStore();

const rejectModal = ref(false);
const approveModal = ref(false);
const viewModal = ref(false);

const handleApproveConfirm = async () => {
  try {
    await UpdateClaimSubmissionStatus(props.item.id, "Approved");
    approveModal.value = false;
    emit("onAction", { id: props.item.id, status: "Approved" });
  } catch (err) {
    console.error("Failed to approve application:", err);
  }
};

const handleRejectConfirm = async () => {
  try {
    await UpdateClaimSubmissionStatus(props.item.id, "Rejected");
    rejectModal.value = false;
    emit("onAction", { id: props.item.id, status: "Rejected" });
  } catch (err) {
    console.error("Failed to reject application:", err);
  }
};

const handleViewContract = () => {
  if (props.item.contractUrl) {
    window.open(props.item.contractUrl, "_blank");
  } else {
    alert("No contract file available for this application.");
  }
};
</script>

<style scoped>
.btn-primary {
  background-color: var(--primary);
}

.btn-outline-primary {
  border-color: var(--accent);
  color: var(--accent);
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.2), 0 2px 10px 0 rgba(0, 0, 0, 0.1);
  vertical-align: middle;
}

.btn-outline-primary i {
  vertical-align: top;
}

.default-avatar {
  width: 50px;
  height: 50px;
  background-color: var(--primary);
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  font-size: 22px;
  font-weight: bold;
}

.profile-avatar {
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-letter {
  text-transform: uppercase;
}
</style>

<style>
.tooltip-inner {
  font-family: "Poppins", sans-serif !important;
  background-color: var(--primary);
  font-size: 12px;
  padding: 3px 12px;
}

.badge-dot {
  background-color: var(--primary);
}
</style>
